import { useEffect, useRef, useState } from "preact/hooks";
import { useSignal, useSignalEffect } from "@preact/signals";
import type { ReadonlySignal } from "@preact/signals";
import type { FormStore } from "@formisch/preact";
import { setInput } from "@formisch/preact";

import { InputLabel } from "./input-label.js";
import { InputError } from "./input-error.js";
import { inputClass, type FieldProps } from "./shared.js";
import { enterKeyHintFor } from "../../autofill.js";
import { autoSelectSuggestion, typedAnchorsAgree, typedStreetAgrees, typedUnitText } from "../../address-match.js";
import {
  lobAteUnitJunk,
  needsVerification,
  recordNotAttempted,
  recordUnverified,
  recordVerified,
  relaxPrimaryLine,
  verifyAddress,
  withUnit,
  type AddressVerificationRecord,
  type LobVerificationInput,
  type LobVerificationResult,
  type MinimumAddressResolution,
} from "../../address-verification.js";
import {
  ADDRESS_QUERY_MIN,
  searchAddresses,
  suggestionLabel,
  type AddressComponents,
  type AddressPick,
  type AddressSuggestion,
} from "../../address-search.js";
import type { FormEmbedField } from "../../types.js";

const DEBOUNCE_MS = 250;

interface AddressInputProps {
  field: FormEmbedField;
  input: ReadonlySignal<any>;
  errors: ReadonlySignal<[string, ...string[]] | null>;
  props: FieldProps;
  disabled: boolean;
  form: FormStore;
  isLast?: boolean;
  /** Base URL of the web proxy (`config.embedApiUrl`). Absent = plain text. */
  searchUrl?: string;
  onPick?: (name: string, pick: AddressPick | null) => void;
  /**
   * Hands the form this field's auto-selection, run before submit: a typed
   * address that a suggestion stands for becomes that pick (see
   * `autoSelectSuggestion`). `null` unregisters on unmount.
   */
  registerAutoSelect?: (name: string, autoSelect: (() => boolean) | null) => void;
  /** The form's minimum address resolution; `street`/`building` turn on Lob. */
  minimumAddressResolution?: MinimumAddressResolution;
  /**
   * Hands the form this field's USPS verification, awaited on submit after
   * auto-selection: resolves true to let the submit through, false to hold it
   * (a street number or unit is needed, or a first warning is shown).
   */
  registerVerify?: (name: string, verify: (() => Promise<boolean>) | null) => void;
  /** This field's `_addressVerification` record (`null` clears it). */
  onRecord?: (name: string, record: AddressVerificationRecord | null) => void;
}

/** Longest a submit-time lookup may hold the submit before it counts as an outage. */
const VERIFY_TIMEOUT_MS = 8000;

/** Run `task` with an abort signal that fires after VERIFY_TIMEOUT_MS; resolve
 *  `fallback` if it times out or throws. */
async function withTimeout<T>(task: (signal: AbortSignal) => Promise<T>, fallback: T): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(fallback);
    }, VERIFY_TIMEOUT_MS);
  });
  try {
    return await Promise.race([task(controller.signal).catch(() => fallback), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** What verification is waiting on from the visitor, shown below the address. */
type Stage = "idle" | "needs_street_number" | "needs_unit";

const MESSAGES = {
  needsStreetNumber: "Please add the street number for this address.",
  needsUnit: "This address needs an apartment, suite, or unit number.",
  emptyStreetNumber: "Enter the street number to continue.",
  emptyUnit: "Enter your apartment, suite, or unit number to continue.",
  warning: "We couldn't verify this address with the postal service. Check it, then submit again to continue.",
  verifying: "Checking your address…",
};

type Results = { query: string; suggestions: AddressSuggestion[] };

/**
 * Street address with Mapbox suggestions — an ARIA 1.2 combobox, written by
 * hand because the SDK is Preact in a Shadow DOM (no React UI kit). The text
 * is always the answer: a typed address that matches nothing still submits.
 * Picking a suggestion also hands the parent its structured parts, which the
 * submit sends as the `_addressComponents` sidecar.
 *
 * Like the widget's address screen, a typed-but-not-picked address settles on
 * the suggestion it stands for — on Enter with nothing highlighted, and on
 * submit (`registerAutoSelect`): the sole suggestion, or the one the text fully
 * spells out. Never over an explicit pick.
 *
 * On submit it verifies the address with USPS (Lob, through `/api/av`) when
 * the form's minimum resolution is `street` or `building` — the widget's rules,
 * inline instead of in a modal: a missing street number or (for `building`) a
 * missing unit adds an input below the address, focused, and holds the
 * submit; an address USPS cannot confirm shows one warning and goes through on
 * the next submit; a verification outage never holds anything. Every outcome
 * is recorded for `_addressVerification`.
 *
 * The list only ever shows results for the text currently in the box —
 * during the debounce and the fetch it shows "Searching…", so an address the
 * visitor has typed past is never pickable. When search is unavailable the
 * list stays closed and the field is a plain text box.
 */
export function AddressInput({
  field,
  input,
  errors,
  props,
  disabled,
  form,
  isLast = false,
  searchUrl,
  onPick,
  registerAutoSelect,
  minimumAddressResolution,
  registerVerify,
  onRecord,
}: AddressInputProps) {
  const text = useSignal<string>(input.value ?? "");
  useSignalEffect(() => {
    text.value = input.value ?? "";
  });

  // The settled text to search for. A new object on every settle — not just
  // on a changed string — so text that returns to an in-flight query whose
  // request was cancelled (70 Wash → 70 Washi → 70 Wash) still searches.
  const [request, setRequest] = useState({ query: "" });
  const query = request.query;
  const [results, setResults] = useState<Results | null>(null);
  const [unavailable, setUnavailable] = useState(!searchUrl);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const abort = useRef<AbortController | null>(null);
  // An explicit selection (click, keyboard, or an earlier auto-select) that
  // the text still holds; auto-selection never overrides it.
  const picked = useRef(false);
  // The picked suggestion's parts and unit — what verification sends to Lob.
  const pickedSuggestion = useRef<AddressSuggestion | null>(null);

  // Verification state (the widget's refs, same names where they map).
  const [stage, setStage] = useState<Stage>("idle");
  const [extra, setExtra] = useState("");
  const [extraError, setExtraError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  const extraRef = useRef<HTMLInputElement | null>(null);
  const runRef = useRef(0);
  const verifiedTextRef = useRef("");
  const failedAttemptsRef = useRef(0);
  const warningShownRef = useRef(false);
  const originalComponentsRef = useRef<AddressComponents | null>(null);
  const originalTextRef = useRef("");
  const originalSecondaryRef = useRef("");
  // The parts to verify for a given text: set by the first verification and by
  // a street-number correction, so a later unit prompt or warning retry works
  // from the corrected street, not the original pick.
  const sourceRef = useRef<{ text: string; components: AddressComponents; secondary: string } | null>(null);
  const lobCache = useRef(new Map<string, LobVerificationResult | null>());
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // `props.name` is formisch's serialized path (`["address"]`), not an id.
  const listId = `fv-${field.name}-suggestions`;
  const optionId = (index: number) => `fv-${field.name}-suggestion-${index}`;
  const typed = text.value.trim();
  const searchable = !unavailable && typed.length >= ADDRESS_QUERY_MIN;
  const current = results !== null && results.query === typed && query === typed;
  const suggestions = searchable && current ? results.suggestions : [];
  const expanded = open && searchable;

  useEffect(() => {
    if (unavailable) return;
    const timer = setTimeout(() => setRequest({ query: typed }), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed, unavailable]);

  useEffect(() => {
    // Only while the visitor is typing here: a prefilled answer nobody
    // touched must not spend a billed search.
    if (!open || !searchUrl || unavailable || query.length < ADDRESS_QUERY_MIN) return;
    // Reopening on unchanged text: the list already answers it.
    if (results?.query === query) return;
    // The text moved past the settled query (a keystroke reopened the list
    // before the debounce caught up): searching the old text would be a billed
    // request whose answer is never shown. The debounce settles the new text.
    if (query !== typed) return;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    searchAddresses(searchUrl, query, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.status === "unavailable") {
          setUnavailable(true);
          setOpen(false);
          return;
        }
        setResults({ query, suggestions: result.suggestions });
        setActive(-1);
      })
      .catch(() => {
        // Aborted: a newer query owns the list.
      });
    return () => controller.abort();
    // `results` is read, not a trigger: a new answer must not re-run this.
  }, [open, request, searchUrl, unavailable, typed]);

  function commit(value: string) {
    text.value = value;
    setInput(form, { path: [field.name] as any, input: value });
  }

  function handleInput(e: Event) {
    // The in-flight search answers text that no longer exists. Cancel it now,
    // not when the debounce settles — an `unavailable` from it would otherwise
    // switch suggestions off for the text the visitor is typing.
    abort.current?.abort();
    commit((e.target as HTMLInputElement).value);
    picked.current = false;
    pickedSuggestion.current = null;
    onPick?.(field.name, null);
    resetVerification();
    setOpen(true);
    setActive(-1);
  }

  function pick(suggestion: AddressSuggestion) {
    const value = suggestionLabel(suggestion);
    commit(value);
    picked.current = true;
    pickedSuggestion.current = suggestion;
    onPick?.(field.name, { value, components: suggestion.components });
    resetVerification();
    // The picked text is its own query; settle on it so the list does not
    // search again for an address that is already chosen.
    setRequest({ query: value.trim() });
    setResults({ query: value.trim(), suggestions: [] });
    setOpen(false);
    setActive(-1);
  }

  /** Settle typed text on the suggestion it stands for. Reads the list as
   *  shown — only results for the text in the box — so a stale answer can
   *  never be auto-selected. */
  function autoSelect(): boolean {
    if (picked.current) return false;
    const chosen = autoSelectSuggestion(typed, suggestions);
    if (!chosen) return false;
    pick(chosen);
    return true;
  }
  // The form calls the latest closure (current text and list), registered once.
  const autoSelectRef = useRef(autoSelect);
  autoSelectRef.current = autoSelect;
  useEffect(() => {
    if (!registerAutoSelect) return;
    registerAutoSelect(field.name, () => autoSelectRef.current());
    return () => registerAutoSelect(field.name, null);
  }, [field.name, registerAutoSelect]);

  // ------------------------------------------------------------------
  // USPS verification (the widget's verify / runVerification / unit flow)
  // ------------------------------------------------------------------

  /** A new address: nothing known about it, nothing pending below it. */
  function resetVerification() {
    runRef.current += 1;
    // A run in flight is superseded and will not clear its own spinner.
    setVerifying(false);
    verifiedTextRef.current = "";
    failedAttemptsRef.current = 0;
    originalComponentsRef.current = null;
    sourceRef.current = null;
    setStage("idle");
    setExtra("");
    setExtraError(null);
    setNotice(null);
    onRecord?.(field.name, null);
  }

  /** One Lob call, cached per input so a resubmit does not bill twice, and
   *  bounded — a stalled request is an outage, which never holds a lead. */
  async function lob(input: LobVerificationInput): Promise<LobVerificationResult | null> {
    const key = JSON.stringify(input);
    if (lobCache.current.has(key)) return lobCache.current.get(key)!;
    const result = await withTimeout((signal) => verifyAddress(searchUrl!, input, signal), null);
    if (result) lobCache.current.set(key, result);
    return result;
  }

  function askFor(next: Exclude<Stage, "idle">) {
    setStage(next);
    setExtra("");
    setExtraError(null);
    setNotice(null);
    setFocusTick((n) => n + 1);
  }

  function markVerified(result: LobVerificationResult) {
    verifiedTextRef.current = text.value.trim();
    setStage("idle");
    setNotice(null);
    setExtraError(null);
    onRecord?.(field.name, recordVerified(result));
  }

  /**
   * A failure the visitor may not be able to fix. The first time (or right
   * away when nothing they could type would help) it is one gentle warning
   * that holds the submit; after that the address is accepted as given — the
   * widget's "warning at most once" courtesy.
   */
  function failure(result: LobVerificationResult, next: Exclude<Stage, "idle">, immediate = false): boolean {
    failedAttemptsRef.current += 1;
    if (immediate || failedAttemptsRef.current >= 2) {
      if (warningShownRef.current) {
        markVerified(result);
        return true;
      }
      warningShownRef.current = true;
      setStage("idle");
      setNotice(MESSAGES.warning);
      setFocusTick((n) => n + 1);
      return false;
    }
    askFor(next);
    return false;
  }

  async function runVerification(components: AddressComponents, secondary: string, run: number): Promise<boolean> {
    const base = {
      city: components.city,
      state: components.state,
      zip_code: components.zip_code,
      ...(secondary ? { secondary_line: secondary } : {}),
    };
    let result = await lob({ primary_line: components.primary_line, ...base });
    let primaryLine = components.primary_line;
    // Second chance: USPS rejects "700e E 9th"-style typos the relaxer fixes.
    if (result?.deliverability === "undeliverable") {
      const relaxed = relaxPrimaryLine(components.primary_line);
      if (relaxed !== components.primary_line) {
        const retry = await lob({ primary_line: relaxed, ...base });
        // An outage on the retry is an outage: the rejection is unconfirmed.
        if (!retry) result = null;
        else if (retry.deliverability !== "undeliverable") {
          result = retry;
          primaryLine = relaxed;
        }
      }
    }
    // The visitor edited the address while Lob answered: hold the submit, the
    // next one verifies what is on screen now.
    if (run !== runRef.current) return false;
    // A unit prompt checks the street USPS accepted, not the one it rejected.
    if (primaryLine !== components.primary_line && originalComponentsRef.current) {
      originalComponentsRef.current = { ...originalComponentsRef.current, primary_line: primaryLine };
    }
    if (!result) {
      onRecord?.(field.name, recordUnverified("lob_unavailable"));
      return true;
    }

    // Lob can misparse a word as the primary number — it must hold a digit.
    const hasStreetNumber = !!result.components.primary_number && /\d/.test(result.components.primary_number);
    if (result.deliverability === "undeliverable" && hasStreetNumber) {
      // The address does not exist; nothing typed below would fix it.
      return failure(result, "needs_street_number", true);
    }
    if (!hasStreetNumber) return failure(result, "needs_street_number");
    if (minimumAddressResolution === "building" && result.deliverability === "deliverable_missing_unit") {
      askFor("needs_unit");
      return false;
    }
    // A unit carried with a suggestion (taken from the visitor's text) gets
    // the unit prompt's check: one USPS rejects, or Lob only partly honored,
    // is not verified — one warning, then accepted as given.
    if (
      secondary &&
      (result.deliverability === "deliverable_incorrect_unit" || lobAteUnitJunk(secondary, result))
    ) {
      return failure(result, "needs_unit", true);
    }
    markVerified(result);
    return true;
  }

  async function verifyUnit(unit: string, run: number): Promise<boolean> {
    const original = originalComponentsRef.current!;
    const result = await lob({
      primary_line: original.primary_line,
      secondary_line: unit,
      city: original.city,
      state: original.state,
      zip_code: original.zip_code,
    });
    if (run !== runRef.current) return false;
    // The unit joins the answer either way — the visitor typed it.
    const value = withUnit(originalTextRef.current, unit);
    commit(value);
    if (picked.current) onPick?.(field.name, { value, components: original });
    if (!result) {
      // An outage never holds a lead — the unit stays in the address as typed.
      verifiedTextRef.current = value.trim();
      setStage("idle");
      setNotice(null);
      onRecord?.(field.name, recordUnverified("lob_unavailable"));
      return true;
    }
    const accepted =
      result.deliverability === "deliverable" || result.deliverability === "deliverable_unnecessary_unit";
    if (accepted && !lobAteUnitJunk(unit, result)) {
      markVerified(result);
      return true;
    }
    // Not confirmed: one warning, then accept as typed.
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      setNotice(MESSAGES.warning);
      setFocusTick((n) => n + 1);
      return false;
    }
    verifiedTextRef.current = value.trim();
    setNotice(null);
    onRecord?.(field.name, recordVerified(result));
    return true;
  }

  async function verify(): Promise<boolean> {
    if (!needsVerification(minimumAddressResolution)) {
      if (minimumAddressResolution) onRecord?.(field.name, recordNotAttempted("resolution_below_street"));
      return true;
    }
    if (!searchUrl) {
      onRecord?.(field.name, recordNotAttempted("no_address_autocomplete"));
      return true;
    }
    const current = text.value.trim();
    // Optional and empty: nothing to verify (required is the schema's job).
    if (!current) return true;
    if (stage === "idle" && verifiedTextRef.current === current) return true;

    const run = runRef.current;
    setVerifying(true);
    try {
      if (stage === "needs_unit") {
        const unit = extra.trim();
        if (!unit) {
          setExtraError(MESSAGES.emptyUnit);
          setFocusTick((n) => n + 1);
          return false;
        }
        return await verifyUnit(unit, run);
      }

      if (stage === "needs_street_number") {
        const number = extra.trim();
        if (!number) {
          setExtraError(MESSAGES.emptyStreetNumber);
          setFocusTick((n) => n + 1);
          return false;
        }
        const original = originalComponentsRef.current!;
        const components = { ...original, primary_line: `${number} ${original.primary_line}` };
        const value = `${number} ${originalTextRef.current}`;
        commit(value);
        if (picked.current) onPick?.(field.name, { value, components });
        // The corrected address is the source from here on: a unit prompt,
        // or a warning retry, must not fall back to the street without it.
        originalComponentsRef.current = components;
        originalTextRef.current = value;
        sourceRef.current = { text: value.trim(), components, secondary: originalSecondaryRef.current };
        setStage("idle");
        return await runVerification(components, originalSecondaryRef.current, run);
      }

      // First verification of this text (or a retry after a warning).
      const known = sourceRef.current?.text === current ? sourceRef.current : null;
      let components = known?.components ?? pickedSuggestion.current?.components ?? null;
      let secondary = known ? known.secondary : (pickedSuggestion.current?.secondary_line ?? "");
      if (!components) {
        // Typed, never picked: resolve it the way the widget does — the top
        // suggestion's parts, used for verification only.
        const resolved = await withTimeout(
          (signal) => searchAddresses(searchUrl, current, signal).catch(() => null),
          null,
        );
        if (run !== runRef.current) return false;
        // Only a lookup that agrees with the house number, street and city /
        // ZIP the visitor typed: verifying 72 Washington Street, or one in
        // another state, would describe a different address than the one
        // submitted.
        const first = resolved?.status === "ok" ? resolved.suggestions[0] : undefined;
        const top =
          first && typedStreetAgrees(current, first) && typedAnchorsAgree(current, first) ? first : undefined;
        components = top?.components ?? null;
        // The unit is the one the visitor typed, never the suggestion's: a
        // lookup answering "APT 5B" with the 5A record must not verify 5A.
        secondary = top ? typedUnitText(current, top) : "";
      }
      if (!components) {
        onRecord?.(field.name, recordNotAttempted("no_address_components"));
        return true;
      }
      originalComponentsRef.current = components;
      originalTextRef.current = current;
      originalSecondaryRef.current = secondary;
      sourceRef.current = { text: current, components, secondary };
      return await runVerification(components, secondary, run);
    } finally {
      if (run === runRef.current) setVerifying(false);
    }
  }
  // The builder changed the resolution (preview): what passed under the old
  // rules is not verified under the new ones.
  const resolutionRef = useRef(minimumAddressResolution);
  useEffect(() => {
    if (resolutionRef.current === minimumAddressResolution) return;
    resolutionRef.current = minimumAddressResolution;
    warningShownRef.current = false;
    resetVerification();
  }, [minimumAddressResolution]);
  const verifyRef = useRef(verify);
  verifyRef.current = verify;
  useEffect(() => {
    if (!registerVerify) return;
    registerVerify(field.name, () => verifyRef.current());
    return () => registerVerify(field.name, null);
  }, [field.name, registerVerify]);

  // Bring what verification needs into view and focus — the input below the
  // address, or the address itself for a warning.
  useEffect(() => {
    if (focusTick === 0) return;
    // A prompt focuses its input; a warning brings the address (and the
    // warning under it) into view — the visitor may have submitted from a
    // footer far below.
    const target =
      stage === "idle" ? wrapperRef.current?.querySelector<HTMLInputElement>("input") : extraRef.current;
    target?.focus();
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusTick, stage]);

  function handleKeyDown(e: KeyboardEvent) {
    if (!expanded) {
      if (e.key === "ArrowDown" && searchable) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length > 0) setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (suggestions.length > 0) setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      const chosen = suggestions[active];
      if (chosen) {
        // Picking, not submitting the form.
        e.preventDefault();
        pick(chosen);
      } else if (autoSelect()) {
        // Nothing highlighted, but the text stands for one suggestion: the
        // same intent as clicking it, so pick instead of submitting.
        e.preventDefault();
      } else {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  }

  const errorClass = errors.value ? "border-theme-destructive" : "border-theme-input";
  const status =
    suggestions.length > 0
      ? null
      : current
        ? "No matching addresses. You can keep typing yours."
        : "Searching…";

  return (
    <div ref={wrapperRef} class={`flex flex-col gap-1 ${field.width === "half" ? "w-[calc(50%-6px)]" : "w-full"}`} data-fv-field={field.name}>
      <InputLabel label={field.label} required={field.required} description={field.description} />
      <div class="relative">
        <input
          {...props}
          class={`${inputClass} ${errorClass} w-full`}
          type="text"
          // Browser autofill would cover the suggestion list.
          autocomplete="off"
          enterkeyhint={enterKeyHintFor(isLast)}
          placeholder={field.placeholder || "Start typing your address..."}
          value={text}
          disabled={disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            setOpen(false);
            props.onBlur?.(e as any);
          }}
          role={unavailable ? undefined : "combobox"}
          aria-autocomplete={unavailable ? undefined : "list"}
          aria-expanded={unavailable ? undefined : expanded}
          aria-controls={unavailable ? undefined : listId}
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          aria-invalid={!!errors.value}
          aria-errormessage={`${props.name}-error`}
        />
        {expanded && (
          <div class="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-theme border border-theme-border bg-theme-card shadow-lg">
            {status && (
              <div class="px-3 py-2.5 text-sm text-theme-muted-fg" role="status">
                {status}
              </div>
            )}
            <ul id={listId} role="listbox" aria-label={field.label} class="m-0 max-h-64 list-none overflow-y-auto p-1 empty:hidden">
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.mapbox_id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === active}
                    // Keep focus in the input so blur does not close the list
                    // before the click lands.
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(suggestion)}
                    // Lower-confidence matches are dimmed so visitors reach for
                    // the exact/high ones first (the widget's rule).
                    class={`cursor-pointer rounded-theme px-3 py-2 text-sm ${index === active ? "bg-theme-muted" : ""} ${suggestion.confidence === "medium" || suggestion.confidence === "low" ? "opacity-60" : ""}`}
                  >
                    <span class="flex flex-wrap items-center gap-1.5">
                      <span class="break-words font-medium text-theme-fg">{suggestion.line1}</span>
                      {suggestion.unit_extrapolated && (
                        <span class="rounded-theme bg-theme-muted px-1.5 py-px text-[11px] font-medium text-theme-muted-fg">
                          unit not verified
                        </span>
                      )}
                    </span>
                    {suggestion.line2 && (
                      <span class="block break-words text-xs text-theme-muted-fg">{suggestion.line2}</span>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
      <InputError name={props.name} errors={errors} />
      {stage !== "idle" && (
        <div class="mt-1 flex flex-col gap-1">
          <label class="text-sm font-medium text-theme-fg" for={`fv-${field.name}-extra`}>
            {stage === "needs_unit" ? "Apartment, suite, or unit" : "Street number"}
            <span class="text-theme-destructive"> *</span>
          </label>
          <input
            ref={extraRef}
            id={`fv-${field.name}-extra`}
            class={`${inputClass} ${extraError ? "border-theme-destructive" : "border-theme-input"} w-full`}
            type="text"
            inputmode={stage === "needs_street_number" ? "numeric" : undefined}
            autocomplete={stage === "needs_unit" ? "address-line2" : "off"}
            placeholder={stage === "needs_unit" ? "e.g. Apt 5B" : "e.g. 123"}
            value={extra}
            disabled={disabled}
            onInput={(e) => {
              setExtra((e.target as HTMLInputElement).value);
              setExtraError(null);
            }}
            aria-invalid={true}
            aria-describedby={`fv-${field.name}-extra-message`}
          />
          <div id={`fv-${field.name}-extra-message`} class="text-xs text-theme-destructive" role="alert">
            {extraError ?? (stage === "needs_unit" ? MESSAGES.needsUnit : MESSAGES.needsStreetNumber)}
          </div>
        </div>
      )}
      {notice && (
        <div class="text-xs text-theme-destructive" role="alert">
          {notice}
        </div>
      )}
      {verifying && (
        <div class="text-xs text-theme-muted-fg" role="status">
          {MESSAGES.verifying}
        </div>
      )}
    </div>
  );
}
